import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useLocation, useParams } from 'react-router-dom';
// Icons
import { Button, Dropdown, Menu, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
// API
import { GET_CREDIT_USAGE_REPORT } from 'api/campaignV2/queries';
// Types
import {
  GetCreditUsageReport,
  GetCreditUsageReport_getAgencies_entities,
  GetCreditUsageReportVariables,
} from 'api/campaignV2/types/GetCreditUsageReport';
import { AgencyListOrderBy, SortDirection } from 'api/graphql-global-types';
// Constants
import {
  CREDIT_USAGE_REPORT,
  CREDIT_USAGE_DETAILS_GEN,
} from 'constants/routes';
import { onlyDateTimeFormat } from 'constants/global';
// UI
import { DEFAULT_CURRENT_PAGE } from 'ui/Table';
import Table from 'ui/Table';
import { SortedInfo, TableFilter } from 'ui/Table';

const { Title } = Typography;

const CreditUsageReport: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { itemsPerPage, pageNumber } = useParams<{
    itemsPerPage?: string;
    pageNumber?: string;
  }>();

  const [pageSize, setPageSize] = useState<number>(Number(itemsPerPage || 10));
  const [currentPage, setCurrentPage] = useState<number>(
    Number(pageNumber || DEFAULT_CURRENT_PAGE)
  );

  const handleSetPagination = useCallback(
    (page: number, size: number): void => {
      history.push(`${CREDIT_USAGE_REPORT}/${size}/${page}${location.search}`);
      setCurrentPage(page);
      setPageSize(size);
    },
    [history, location.search]
  );

  useEffect(() => {
    if (itemsPerPage && Number(itemsPerPage) !== pageSize) {
      setPageSize(Number(itemsPerPage));
    }
  }, [itemsPerPage, pageSize]);

  useEffect(() => {
    if (pageNumber && Number(pageNumber) !== currentPage) {
      setCurrentPage(Number(pageNumber));
    }
  }, [pageNumber, currentPage]);

  const [sort, setSortInfo] = useState<SortedInfo<AgencyListOrderBy>>(() => {
    const searchParams = new URLSearchParams(location.search);
    const sortBy = searchParams.get('sortBy') || AgencyListOrderBy.firstName;
    const sortOrder =
      (searchParams.get('sortOrder') as SortDirection) || SortDirection.ASC;
    return {
      order: sortOrder,
      key: sortBy as AgencyListOrderBy,
    };
  });

  const [searchFilters, setSearchFilters] = useState<TableFilter[]>(() => {
    const searchParams = new URLSearchParams(location.search);
    const filters: TableFilter[] = [];

    searchParams.forEach((value, key) => {
      if (!['page', 'pageSize', 'sortBy', 'sortOrder'].includes(key)) {
        const parsedValue = value.includes(',') ? value.split(',') : value;
        filters.push({ key, value: parsedValue });
      }
    });

    return filters;
  });

  useEffect(() => {
    if (!itemsPerPage || !pageNumber) {
      const newPath = `${CREDIT_USAGE_REPORT}/${pageSize}/${currentPage}${location.search}`;
      if (location.pathname !== newPath) {
        history.replace(newPath);
      }
    }
  }, [
    itemsPerPage,
    pageNumber,
    pageSize,
    currentPage,
    location.pathname,
    location.search,
    history,
  ]);

  const isUpdatingFromUrl = React.useRef(false);
  const prevSearchRef = React.useRef(location.search);

  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      prevSearchRef.current = location.search;
      return;
    }

    if (location.search === prevSearchRef.current) {
      return;
    }

    prevSearchRef.current = location.search;

    const searchParams = new URLSearchParams(location.search);
    const sortByParam = searchParams.get('sortBy');
    const sortOrderParam = searchParams.get('sortOrder');

    if (sortByParam || sortOrderParam) {
      const newSortBy = sortByParam || AgencyListOrderBy.firstName;
      const newSortOrder =
        (sortOrderParam as SortDirection) || SortDirection.ASC;
      if (newSortBy !== sort.key || newSortOrder !== sort.order) {
        setSortInfo({
          order: newSortOrder,
          key: newSortBy as AgencyListOrderBy,
        });
      }
    }

    const filtersFromUrl: TableFilter[] = [];
    searchParams.forEach((value, key) => {
      if (!['sortBy', 'sortOrder'].includes(key)) {
        const parsedValue = value.includes(',') ? value.split(',') : value;
        filtersFromUrl.push({ key, value: parsedValue });
      }
    });

    const currentFiltersStr = JSON.stringify(searchFilters);
    const urlFiltersStr = JSON.stringify(filtersFromUrl);
    if (currentFiltersStr !== urlFiltersStr) {
      setSearchFilters(filtersFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (
      sort.key !== AgencyListOrderBy.firstName ||
      sort.order !== SortDirection.ASC
    ) {
      params.set('sortBy', sort.key);
      params.set('sortOrder', sort.order);
    }

    searchFilters.forEach(({ key, value }) => {
      const stringValue = Array.isArray(value)
        ? value.join(',')
        : String(value);
      params.set(key, stringValue);
    });

    const newSearch = params.toString();
    const currentSearch = location.search.replace('?', '');
    if (newSearch !== currentSearch) {
      isUpdatingFromUrl.current = true;
      const basePath = location.pathname;
      history.push({
        pathname: basePath,
        search: newSearch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, searchFilters]);

  const getAgenciesInput = () => {
    const input: any = {
      direction: sort.order,
      orderBy: sort.key,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      input[key] = value;
    });

    return { input };
  };

  const { data, loading, error } = useQuery<
    GetCreditUsageReport,
    GetCreditUsageReportVariables
  >(GET_CREDIT_USAGE_REPORT, {
    variables: getAgenciesInput(),
    fetchPolicy: 'cache-and-network',
  });

  const handleViewDetails = (
    record: GetCreditUsageReport_getAgencies_entities
  ): void => {
    history.push({
      pathname: `${CREDIT_USAGE_DETAILS_GEN}/${record.id}`,
      state: {
        returnTo: location.pathname + location.search,
        agencyName: `${record.firstName} ${record.lastName}`,
        agencyEmail: record.email,
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
    },
    {
      title: 'Company Name',
      dataIndex: ['agencyDetails', 'companyName'],
      key: 'companyName',
      width: 200,
      withSearch: true,
    },
    {
      title: 'User Name',
      dataIndex: 'firstName',
      key: 'userName',
      width: 180,
      render: (_: any, record: GetCreditUsageReport_getAgencies_entities) =>
        `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'User Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      withSearch: true,
    },
    {
      title: 'Credits Remaining',
      dataIndex: 'creditsRemaining',
      key: 'creditsRemaining',
      width: 150,
      render: (value: number | null) => value ?? 0,
    },
    {
      title: 'Credits Used',
      dataIndex: 'creditsUsed',
      key: 'creditsUsed',
      width: 130,
      render: (value: number | null) => value ?? 0,
    },
    {
      title: 'Last Date Credit Used',
      dataIndex: 'lastDateCreditUsed',
      key: 'lastDateCreditUsed',
      width: 180,
      render: (value: string | null) =>
        value ? moment(value).format(onlyDateTimeFormat) : '-',
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: GetCreditUsageReport_getAgencies_entities) => {
        const menu = (
          <Menu>
            <Menu.Item key="details" onClick={() => handleViewDetails(record)}>
              Details
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown
            overlay={menu}
            trigger={['click']}
            disabled={
              loading || (!record.creditsUsed && !record.lastDateCreditUsed)
            }
          >
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (error) {
    return <div>Error loading credit usage report: {error.message}</div>;
  }

  return (
    <div>
      <Title level={2}>Credit Usage Report</Title>
      <Table<GetCreditUsageReport_getAgencies_entities, AgencyListOrderBy>
        columns={columns}
        data={data?.getAgencies.entities || []}
        scroll={{ x: 1200 }}
        loading={loading}
        total={data?.getAgencies.total}
        defaultPageSize={pageSize}
        defaultCurrent={currentPage}
        setPageAndSize={handleSetPagination}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />
    </div>
  );
};

export default CreditUsageReport;
